import { useEffect, useState, type FormEvent } from 'react'
import type { ZebraPrinter, ZebraPrinterPayload, ZebraTemplate } from '@/api/labelPrintingApi'
import { useLabelPrintingMutations, useLabelPrintingOptions } from '@/hooks/useLabelPrinting'
import {
  CONNECTION_LABEL,
  DEFAULT_ZEBRA_PORT,
  ZEBRA_CONNECTIONS,
  parsePort,
  pruneZebraAllowed,
  validateZebraPrinter,
  zebraAllowedChoices,
  type ZebraPrinterDraft,
  type ZebraPrinterErrors,
} from '@/lib/labelPrinting'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AllowedTemplatesField, Field, LocationInput, SwitchField, activeNote, signatureNote } from './shared'

function draftOf(p: ZebraPrinter | undefined, firstStock: string, firstResolution: string): ZebraPrinterDraft {
  return p
    ? {
        displayName: p.displayName,
        connection: p.connection,
        host: p.host,
        port: String(p.port),
        serverQueueName: p.serverQueueName ?? '',
        resolution: p.resolution,
        loadedStock: p.loadedStock,
        // Retired links included: they stay on the printer.
        allowedTemplateIds: p.allowedTemplates.map(t => t.id),
        location: p.location ?? '',
        signatureRequired: p.signatureRequired,
        active: p.active,
      }
    : {
        displayName: '',
        connection: 'direct',
        host: '',
        port: String(DEFAULT_ZEBRA_PORT),
        serverQueueName: '',
        resolution: firstResolution,
        loadedStock: firstStock,
        allowedTemplateIds: [],
        location: '',
        // Off by default for Zebra (requirement 24); on for ISBT.
        signatureRequired: false,
        active: true,
      }
}

const errorText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback)

/** Add or edit a Zebra printer. No test print for Zebra in this ticket. */
export function ZebraPrinterDialog({
  printer,
  templates,
  locations,
  onClose,
  onDone,
}: {
  printer?: ZebraPrinter
  templates: ZebraTemplate[]
  locations: string[]
  onClose: () => void
  onDone: (message: string) => void
}) {
  const options = useLabelPrintingOptions()
  const stocks = options.data?.zebraStocks ?? []
  const resolutions = options.data?.zebraResolutions ?? []
  const { createZebraPrinter, updateZebraPrinter } = useLabelPrintingMutations()

  const [draft, setDraft] = useState(() => draftOf(printer, stocks[0] ?? '', resolutions[0] ?? ''))
  const [touched, setTouched] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)

  // Options may arrive after a new draft was made: take the first size and
  // resolution then. The selects offer no empty choice, so '' means unpicked.
  const firstStock = stocks[0]
  const firstResolution = resolutions[0]
  useEffect(() => {
    if (printer) return
    setDraft(d => ({
      ...d,
      loadedStock: d.loadedStock === '' && firstStock !== undefined ? firstStock : d.loadedStock,
      resolution: d.resolution === '' && firstResolution !== undefined ? firstResolution : d.resolution,
    }))
  }, [printer, firstStock, firstResolution])

  const linked = printer?.allowedTemplates ?? []
  const choices = zebraAllowedChoices(templates, draft.loadedStock, draft.resolution, linked)
  const allErrors = validateZebraPrinter(draft, choices)
  // The port check is live once something is typed; the rest after a submit attempt.
  const errors: ZebraPrinterErrors = touched ? allErrors : draft.port ? { port: allErrors.port } : {}
  const saving = createZebraPrinter.isPending || updateZebraPrinter.isPending

  const set = <K extends keyof ZebraPrinterDraft>(key: K, value: ZebraPrinterDraft[K]) =>
    setDraft(d => ({ ...d, [key]: value }))

  // A new size or resolution drops templates that no longer match (requirement 26).
  function changeFit(patch: { loadedStock?: string; resolution?: string }) {
    setDraft(d => {
      const next = { ...d, ...patch }
      return {
        ...next,
        allowedTemplateIds: pruneZebraAllowed(
          d.allowedTemplateIds,
          [...templates, ...linked],
          next.loadedStock,
          next.resolution,
        ),
      }
    })
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    setTouched(true)
    const port = parsePort(draft.port)
    if (Object.keys(allErrors).length > 0 || port === null) return
    setServerError(null)
    const name = draft.displayName.trim()
    const payload: ZebraPrinterPayload = {
      displayName: name,
      connection: draft.connection,
      host: draft.host.trim(),
      port,
      // Only a print server has a queue name.
      serverQueueName: draft.connection === 'print_server' ? draft.serverQueueName.trim() : null,
      resolution: draft.resolution,
      loadedStock: draft.loadedStock,
      location: draft.location.trim() || null,
      signatureRequired: draft.signatureRequired,
      allowedTemplateIds: draft.allowedTemplateIds,
    }
    const done = {
      onSuccess: () => onDone(`Saved “${name}”.`),
      onError: (err: unknown) => setServerError(errorText(err, 'Could not save the printer.')),
    }
    if (printer) updateZebraPrinter.mutate({ id: printer.id, payload: { ...payload, active: draft.active } }, done)
    else createZebraPrinter.mutate(payload, done)
  }

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-xl'>
        <DialogHeader>
          <DialogTitle>{printer ? 'Edit Zebra printer' : 'New Zebra printer'}</DialogTitle>
          <DialogDescription>A network label printer for supplies, equipment, reagents and kits.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} noValidate className='flex flex-col gap-4'>
          <Field id='zebra-printer-name' label='Display name' error={errors.displayName}>
            <Input
              id='zebra-printer-name'
              value={draft.displayName}
              autoFocus
              autoComplete='off'
              aria-invalid={!!errors.displayName}
              aria-describedby={errors.displayName ? 'zebra-printer-name-error' : undefined}
              onChange={e => set('displayName', e.target.value)}
            />
          </Field>

          <fieldset className='flex flex-col gap-2'>
            <legend className='mb-1.5 text-sm font-medium'>Connection</legend>
            <div className='flex flex-wrap gap-4'>
              {ZEBRA_CONNECTIONS.map(c => (
                <label key={c} className='flex items-center gap-2 text-sm'>
                  <input
                    type='radio'
                    name='zebra-printer-connection'
                    value={c}
                    checked={draft.connection === c}
                    onChange={() => set('connection', c)}
                    className='h-4 w-4 accent-primary'
                  />
                  {CONNECTION_LABEL[c]}
                </label>
              ))}
            </div>
          </fieldset>

          <div className='flex gap-3'>
            <Field id='zebra-printer-host' label='Host' error={errors.host} className='flex-[3]'>
              <Input
                id='zebra-printer-host'
                value={draft.host}
                autoComplete='off'
                spellCheck={false}
                aria-invalid={!!errors.host}
                aria-describedby={errors.host ? 'zebra-printer-host-error' : undefined}
                onChange={e => set('host', e.target.value)}
              />
            </Field>
            <Field id='zebra-printer-port' label='Port' error={errors.port}>
              <Input
                id='zebra-printer-port'
                inputMode='numeric'
                value={draft.port}
                autoComplete='off'
                aria-invalid={!!errors.port}
                aria-describedby={errors.port ? 'zebra-printer-port-error' : undefined}
                onChange={e => set('port', e.target.value)}
              />
            </Field>
          </div>
          <p className='-mt-2 text-xs text-muted-foreground'>
            Reachable over the site’s private network. Never a public address.
          </p>

          {draft.connection === 'print_server' && (
            <Field id='zebra-printer-server-queue' label='Server queue name' error={errors.serverQueueName}>
              <Input
                id='zebra-printer-server-queue'
                value={draft.serverQueueName}
                autoComplete='off'
                spellCheck={false}
                aria-invalid={!!errors.serverQueueName}
                aria-describedby={errors.serverQueueName ? 'zebra-printer-server-queue-error' : undefined}
                onChange={e => set('serverQueueName', e.target.value)}
              />
            </Field>
          )}

          <div className='flex gap-3'>
            <Field id='zebra-printer-resolution' label='Resolution'>
              <Select value={draft.resolution} onValueChange={v => changeFit({ resolution: v })}>
                <SelectTrigger id='zebra-printer-resolution'>
                  <SelectValue placeholder='Select a resolution' />
                </SelectTrigger>
                <SelectContent>
                  {resolutions.map(r => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field id='zebra-printer-stock' label='Loaded label size'>
              <Select value={draft.loadedStock} onValueChange={v => changeFit({ loadedStock: v })}>
                <SelectTrigger id='zebra-printer-stock'>
                  <SelectValue placeholder='Select a size' />
                </SelectTrigger>
                <SelectContent>
                  {stocks.map(s => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <AllowedTemplatesField
            legend={`Allowed templates · ${draft.loadedStock} at ${draft.resolution}`}
            choices={choices}
            selected={draft.allowedTemplateIds}
            onChange={ids => set('allowedTemplateIds', ids)}
            emptyText='No approved Zebra template fits this label size and resolution.'
            error={errors.allowed}
          />

          <Field id='zebra-printer-location' label='Department / location'>
            <LocationInput
              id='zebra-printer-location'
              value={draft.location}
              onChange={v => set('location', v)}
              suggestions={locations}
            />
          </Field>

          <SwitchField
            id='zebra-printer-signature'
            label='Signature required to print'
            checked={draft.signatureRequired}
            onCheckedChange={v => set('signatureRequired', v)}
            note={signatureNote(draft.signatureRequired, false)}
          />

          {printer && (
            <SwitchField
              id='zebra-printer-active'
              label='Active'
              checked={draft.active}
              onCheckedChange={v => set('active', v)}
              note={activeNote(draft.active)}
            />
          )}

          {serverError && (
            <p role='alert' className='text-sm text-destructive'>
              {serverError}
            </p>
          )}

          <DialogFooter>
            <Button type='button' variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button type='submit' disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
